import { useEffect, useRef, useState } from 'react';
import { ReactSVG } from 'react-svg';
import { useSelector } from 'react-redux';
import dynamicfunction from '../../utils/dynamicfunction';
import { cn, getContentKeyAccToLang } from '../../utils/utils';
import ViewItems from '../ViewItems';
import { COLORS, fontSizes } from '../../constants/constants';
import Text from '../Text';
import ViewReverse from '../ViewReverse';
import icons from '../../constants/icons';
import { getRejectedFieldData, getSectionRejectedFields, iconColor, isRequiredEmpty } from '../../utils/functions';
import FlagComponent from '../FlagComponent';
import ErrorText from './ErrorText';
import SarSymbol from '../SarSymbol';

const Dropdown = ({
  label,
  labelColor,
  options,
  value,
  onSelect,
  className,
  children,
  valueKey,
  isRequired = true,
  isDisabled,
  placeholder,
  isSearch = false,
  menuClasses,
  optionsClasses,
  innerLabel,
  isPhone = false,
  labelDivClass,
  isLabelHeight = false,
  singleOptionClass,
  valueVeight,
  error,
  isMultiSelect = false,
  array,
  isClose = true,
  isRightSymbol,
  symbol,
  remarksMetadata = null,
  isRemarks = true,
  isPortfolio,
  arrowClasses,
}) => {
  const { isArabic, TextWeight } = dynamicfunction();

  const dropdownRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');

  const dashboardContent = useSelector((state) => state.content?.dashboardContent);
  const { rejection_summary, is_rejected, field_codes } = useSelector((state) => state.corporate);

  const rejectedFields = getSectionRejectedFields(rejection_summary, remarksMetadata?.sectionId);
  const rejectedField = rejectedFields?.length < 1 ? null : getRejectedFieldData(rejectedFields, field_codes, remarksMetadata?.key);

  const isFieldDisabled = isDisabled || (is_rejected && remarksMetadata && (!rejectedField?.remarks || rejectedField?.remarks?.length < 1));

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef?.current?.contains(event?.target)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSelect = (item) => {
    onSelect(item);
    setSearch('');
    isClose && setIsOpen(false);
  };

  const getPlaceholderKey = (key) => {
    return getContentKeyAccToLang(dashboardContent, key, isArabic);
  };

  const filteredOptions = options?.filter((item) => item?.[valueKey]?.toLowerCase()?.includes(search?.toLowerCase()));

  return (
    <ViewItems className={cn('w-full', className)}>
      {label && (
        <div data-required-empty={isRequiredEmpty(isRequired, label, isMultiSelect ? array : value)} className={cn(isLabelHeight ? 'md:h-9' : 'h-6', labelDivClass)}>
          <Text
            text={label}
            size={fontSizes.h14}
            color={labelColor ? labelColor : 'text-primary-light'}
            className={cn('leading-4', isLabelHeight && 'mb-1')}
            isRequired={isRequired}
            isTruncate={false}
          />
        </div>
      )}

      <div className='w-full relative' ref={dropdownRef}>
        <ViewReverse
          className={cn(
            'w-full bg-white py-[8.5px] h-[40px] px-3 justify-between items-center rounded border gap-2 border-grey',
            isFieldDisabled ? 'cursor-not-allowed !bg-disabled' : 'cursor-pointer',
            menuClasses,
            rejectedField?.remarks?.length > 0 && 'border-red-600',
          )}
          onClick={() => {
            !isFieldDisabled && setIsOpen(isSearch && !isPhone ? true : !isOpen);
          }}
        >
          {children ? (
            children
          ) : (
            <ViewItems className='flex-1'>
              {innerLabel && <Text text={innerLabel} color='text-primary-light' />}
              {isSearch && isOpen ? (
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className='w-full outline-none text-sm'
                  placeholder={getPlaceholderKey('search')}
                />
              ) : (
                <Text
                  text={isMultiSelect ? (value || getPlaceholderKey('select')) : value ? value?.[valueKey] : placeholder || getPlaceholderKey('select')}
                  size={fontSizes.h14}
                  weight={valueVeight}
                  color={isFieldDisabled || !value ? 'text-dark-grey/50' : rejectedField?.remarks?.length > 0 ? 'text-red-600' : 'text-black'}
                  className={'line-clamp-1'}
                  isTruncate={false}
                />
              )}
            </ViewItems>
          )}
          <ViewReverse className={'items-center gap-1'}>
            {isRightSymbol &&
              (symbol ? (
                <Text text={symbol} size={fontSizes.h10} color={isFieldDisabled ? 'text-dark-grey/50' : 'text-black'} isTruncate={false} />
              ) : (
                <SarSymbol color={isFieldDisabled ? 'text-dark-grey/50' : 'text-black'} />
              ))}
            <ReactSVG src={icons.downArrow} beforeInjection={(svg) => iconColor(svg, isFieldDisabled ? COLORS.text_grey + '50' : rejectedField?.remarks?.length > 0 ? COLORS.red : COLORS.black)} className={arrowClasses} />
          </ViewReverse>
        </ViewReverse>

        {isOpen && (isSearch ? true : filteredOptions?.length > 0) && (
          <div
            className={cn(
              'border border-grey rounded absolute z-[200] shadow-lg top-11 w-full bg-white max-h-[14.3rem] transition-all duration-200 overflow-auto',
              optionsClasses,
            )}
          >
            {isPhone && (
              <div className='sticky top-0 bg-white z-10 p-2 border-b border-grey'>
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className={cn('w-full outline-none text-sm', isArabic && "text-end")}
                  placeholder={getPlaceholderKey('search')}
                />
              </div>
            )}
            {filteredOptions?.map((item, index) => {
              const isSelected = isMultiSelect ? array?.includes(item?.id) : (value?.[valueKey] ?? value) === item?.[valueKey];
              return (
                <div
                  key={index}
                  className={cn(
                    'border-b border-b-grey p-2 cursor-pointer hover:bg-grey/30 transition-all duration-200',
                    index === filteredOptions?.length - 1 && 'border-b-0',
                    singleOptionClass,
                    isMultiSelect && array?.includes(item?.id) ? 'bg-primary/10' : '',
                    isPortfolio && (isSelected ? 'bg-light-blue/10 border-b-0' : 'border-b-0'),
                  )}
                  onClick={() => handleSelect(item)}
                >
                  {isPhone ? (
                    <ViewReverse className={'gap-2'}>
                      <FlagComponent code={item?.code} />
                      <Text text={item?.[valueKey]} size={fontSizes.h14} />
                      <Text text={`(${item?.dial_code ?? ''})`} size={fontSizes.h14} />
                    </ViewReverse>
                  ) : isPortfolio ? (
                    <ViewReverse className={'gap-2 flex-1 items-center px-2 py-1'}>
                      <ViewItems className={'flex-1'}>
                        <Text
                          text={item?.[valueKey]}
                          color={isSelected ? "text-light-blue" : "text-dark-grey"}
                          size={fontSizes.h13}
                          weight={isSelected && TextWeight.bold}
                          className={'flex-1'}
                          isTruncate={false}
                        />
                        <Text text={item?.portfolioCode} size={fontSizes.h11} color={'text-text-grey'} />
                      </ViewItems>
                      {isSelected && <ReactSVG src={icons.blue_tick_icon} />}
                    </ViewReverse>
                  ) : (
                    <Text text={item?.[valueKey]} size={fontSizes.h14} />
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>
      {error?.length > 0 && <ErrorText text={error} />}
      {
        rejectedField?.remarks?.length > 0 && isRemarks && (
          <ViewReverse className='items-center gap-2 mt-2'>
            <ReactSVG src={icons.field_info_outline} />
            <ErrorText text={rejectedField?.remarks} className={'mt-0'} />
          </ViewReverse>
        )
      }
    </ViewItems>
  );
};

export default Dropdown;
